/**
 * Качи — a list of places taken in from a CSV (#435), shown before anything is saved.
 *
 * The file is the export's shape, or any file with at least the address and the two
 * coordinates. The preview says what each row would be: new, a conflict with a place already
 * here — never written over, since locked means locked — or a row that could not be read.
 * Only the new rows are added, approved at once, because the administrator chose the file.
 *
 * "Already here" is the same words by default. Joining addresses that only read alike —
 * Бряст and завод Бряст — is a switch the administrator turns on, and the preview is asked
 * again when they do: an old system's list joins its duplicates and its neighbours alike, and
 * a pin too many on the map is easier to find and remove than a door that was never added.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { notifyPlacesChanged, placesApi, type PlaceImportResult } from '../../places/api';
import { Banner, Button, Dialog, FileDrop, Pill, Switch, Table, Td, Th, Tr } from '../../ui';
import { describe } from '../errors';

export function PlaceImportDialog({
  open,
  onClose,
  onImported,
}: {
  open: boolean;
  onClose: () => void;
  onImported: (added: number) => void;
}) {
  const { t } = useTranslation();

  const [csv, setCsv] = useState<string | null>(null);
  const [chosen, setChosen] = useState<{ name: string; size: number } | null>(null);
  const [joinSimilar, setJoinSimilar] = useState(false);
  const [preview, setPreview] = useState<PlaceImportResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  /** Changes on every close, so the file box starts empty and the same file can be chosen again. */
  const [picks, setPicks] = useState(0);

  const fresh = preview?.rows.filter((row) => row.status === 'new').length ?? 0;

  function close() {
    setCsv(null);
    setChosen(null);
    setJoinSimilar(false);
    setPreview(null);
    setProblem(null);
    setPicks((was) => was + 1);
    onClose();
  }

  async function read(file: File) {
    setBusy(true);
    setProblem(null);
    setChosen({ name: file.name, size: file.size });

    try {
      const text = await file.text();

      setCsv(text);
      setPreview(await placesApi.import(text, false, joinSimilar));
    } catch (failure) {
      setProblem(describe(failure, t, 'settings.couldNotLoad'));
    } finally {
      setBusy(false);
    }
  }

  /** The switch changes what counts as already here, so the preview is asked again. */
  async function rejoin(next: boolean) {
    setJoinSimilar(next);

    if (csv === null) return;

    setBusy(true);
    setProblem(null);

    try {
      setPreview(await placesApi.import(csv, false, next));
    } catch (failure) {
      setProblem(describe(failure, t, 'settings.couldNotLoad'));
    } finally {
      setBusy(false);
    }
  }

  async function commit() {
    if (csv === null) return;

    setBusy(true);
    setProblem(null);

    try {
      const result = await placesApi.import(csv, true, joinSimilar);

      notifyPlacesChanged();
      onImported(result.added);
      close();
    } catch (failure) {
      setProblem(describe(failure, t, 'settings.couldNotSave'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      wide
      title={t('places.import.title')}
      onClose={close}
      actions={
        <>
          <Button onClick={close}>{t('settings.discard')}</Button>
          <Button variant="primary" disabled={busy || fresh === 0} onClick={() => void commit()}>
            {t('places.import.add', { count: fresh })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="m-0 text-secondary text-ink-2">{t('places.import.about')}</p>

        <FileDrop
          accept=".csv,text/csv"
          hint={t('places.import.fileHint')}
          file={chosen}
          disabled={busy}
          reset={picks}
          onFile={(file) => void read(file)}
        />

        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-body font-semibold">{t('places.import.joinSimilar')}</div>
            <p className="m-0 text-secondary text-ink-2">{t('places.import.joinSimilarHelp')}</p>
          </div>
          <Switch
            checked={joinSimilar}
            disabled={busy}
            label={t('places.import.joinSimilar')}
            onChange={(next) => void rejoin(next)}
          />
        </div>

        {problem !== null && <Banner tone="danger">{problem}</Banner>}

        {preview !== null && (
          <div className="max-h-80 overflow-y-auto">
            <Table tight>
              <thead>
                <Tr>
                  <Th numeric>{t('places.import.row')}</Th>
                  <Th>{t('places.building')}</Th>
                  <Th>{t('places.import.status')}</Th>
                </Tr>
              </thead>
              <tbody>
                {preview.rows.map((row) => (
                  <Tr key={row.row}>
                    <Td numeric>{row.row}</Td>
                    <Td>{row.line}</Td>
                    <Td wrap>
                      <Pill tone={row.status === 'new' ? 'ok' : row.status === 'conflict' ? 'warn' : 'danger'} soft>
                        {t(`places.import.${row.status}`)}
                      </Pill>{' '}
                      {/* A row without a point is a file's fault, not a pick from a list: the
                          desk's words for it would send the administrator looking for one. */}
                      {row.error === 'validation.coordinates_required'
                        ? t('places.import.noPoint')
                        : row.error != null &&
                          t(`errors.${row.error}`, {
                            line: row.existing ?? '',
                            name: '',
                            defaultValue: '',
                          })}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </div>
    </Dialog>
  );
}
